import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, Phone, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOVE_TYPES = ["Home Shifting", "Office Relocation", "Car Transport", "Storage & Warehousing", "Packing Only"];
const VOLUMES = ["1 RK", "1 BHK", "2 BHK", "3 BHK", "4 BHK+", "Office Space", "Vehicle Only"];

const STEPS = [
  ["01", "Tell us your move", "Share where, what and when — it takes a minute."],
  ["02", "Get a clear estimate", "Our specialist calls within 30 minutes with one honest quote."],
  ["03", "We carry the rest", "Crew, packing, transport, tracking — all handled."],
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export const QuoteForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", move_type: "Home Shifting",
    from_location: "", to_location: "", move_date: "", goods_volume: "2 BHK", message: "",
  });

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submitQuote = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(formatApiErrorDetail(error.response?.data?.detail) === "Something went wrong. Please try again."
        ? "We couldn't send your request right now. Please call us and we'll help straight away."
        : formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section quote-section" id="quote">
      <div className="container quote-grid">
        <div className="quote-intro">
          <p className="overline">Let's get moving</p>
          <h2>Tell us where <em>you're headed.</em></h2>
          <div className="quote-steps">
            {STEPS.map(([num, title, text]) => (
              <motion.div className="quote-step" data-testid={`quote-step-${num}`} key={num}
                initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <span>{num}</span>
                <div><strong>{title}</strong><p>{text}</p></div>
              </motion.div>
            ))}
          </div>
          <div className="contact-line">
            <span className="contact-line-icon"><Phone size={17} /></span>
            <div>
              <small>Prefer to talk?</small>
              <a data-testid="quote-phone-link" href="tel:+919135667888">91356 67888</a>
              <a data-testid="quote-phone-link-secondary" href="tel:+919135668888">91356 68888</a>
            </div>
          </div>
        </div>

        <motion.form className="quote-form" data-testid="quote-form" onSubmit={submitQuote}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          {submitted ? (
            <div className="success-state" data-testid="quote-success">
              <span><Check size={26} /></span>
              <h3>You're on our moving list.</h3>
              <p>Thanks{form.name ? `, ${form.name}` : ""}. Our specialist will call you within 30 minutes.</p>
              <a className="btn btn-dark" href="#home">Back to home <ArrowRight size={15} /></a>
            </div>
          ) : (
            <>
              <div className="form-title"><span>FREE ESTIMATE</span><h3>Your move, your way</h3></div>
              {submitError && <div className="form-error" data-testid="quote-error" role="alert">{submitError}</div>}
              <div className="form-row">
                <label>Full name<input data-testid="quote-name-input" required name="name" value={form.name} onChange={update} placeholder="e.g. Ananya Sharma" /></label>
                <label>Phone number<input data-testid="quote-phone-input" required name="phone" value={form.phone} onChange={update} placeholder="+91 00000 00000" /></label>
              </div>
              <div className="form-row">
                <label>Moving from<input data-testid="quote-from-input" required name="from_location" value={form.from_location} onChange={update} placeholder="City or locality" /></label>
                <label>Moving to<input data-testid="quote-to-input" required name="to_location" value={form.to_location} onChange={update} placeholder="City or locality" /></label>
              </div>
              <div className="form-row">
                <label>Move type
                  <select data-testid="quote-type-select" name="move_type" value={form.move_type} onChange={update}>
                    {MOVE_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="select-icon" size={15} />
                </label>
                <label>Moving date<input data-testid="quote-date-input" name="move_date" value={form.move_date} onChange={update} placeholder="DD / MM / YYYY" /></label>
              </div>
              <div className="volume-group">
                <span className="volume-label">Goods volume</span>
                <div className="volume-pills" data-testid="quote-volume-group">
                  {VOLUMES.map((v) => (
                    <button type="button" key={v} data-testid={`volume-pill-${v.toLowerCase().replace(/[\s+]+/g, "-")}`}
                      className={`volume-pill ${form.goods_volume === v ? "active" : ""}`}
                      onClick={() => setForm({ ...form, goods_volume: v })}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <label>Email address <span className="optional">(optional)</span><input data-testid="quote-email-input" type="email" name="email" value={form.email} onChange={update} placeholder="you@example.com" /></label>
              <label>Anything special we should know? <span className="optional">(optional)</span>
                <textarea data-testid="quote-message-input" name="message" value={form.message} onChange={update} rows={2} placeholder="Fragile items, floors, pets, vehicle model…" />
              </label>
              <button data-testid="quote-submit-button" className="btn btn-gold form-submit" type="submit" disabled={sending}>
                {sending ? "Sending…" : "Get my free estimate"} <ArrowRight size={16} />
              </button>
              <small className="form-footnote"><ShieldCheck size={14} /> Your details are private. No spam, ever.</small>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
};
